import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class EqShoppableVideoComponent extends Component {
  requiredRefs = ['rail', 'dialog'];

  #activeIndex = 0;
  #isMuted = false;
  #lastTrigger = null;
  #animationFrame = null;
  #abortController = null;
  #dragState = null;
  #suppressClickUntil = 0;
  #inviewObserver = null;
  #mediaObserver = null;
  #mediaMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const { signal } = this.#abortController;

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate,
      { signal }
    );

    this.#setupDragScroll(signal);

    this.refs.rail?.addEventListener('scroll', this.#requestProgressUpdate, {
      passive: true,
      signal,
    });

    window.addEventListener('resize', this.#requestProgressUpdate, {
      passive: true,
      signal,
    });

    this.refs.dialog?.addEventListener('close', this.#handleDialogClose, {
      signal,
    });

    this.refs.dialog?.addEventListener('click', this.#handleDialogBackdropClick, {
      signal,
    });

    for (const slide of this.#slides) {
      const video = slide.querySelector('video');

      if (!(video instanceof HTMLVideoElement)) continue;

      video.addEventListener('timeupdate', this.#handleVideoProgress, {
        signal,
      });
    }

    // Defer card media (posters and images) until nearing the viewport.
    this.#mediaObserver = new IntersectionObserver(
      (entries) => {
        for (const { target, isIntersecting } of entries) {
          if (!isIntersecting) continue;
          if (target.dataset.poster && !target.poster) {
            target.poster = target.dataset.poster;
          }
          if (target.dataset.srcset && !target.srcset) {
            target.srcset = target.dataset.srcset;
          }
          if (target.dataset.src && !target.src) {
            target.src = target.dataset.src;
          }
          this.#mediaObserver?.unobserve(target);
        }
      },
      // `rootMargin` stops at the rail's overflow clip; `scrollMargin` widens the rail itself.
      { rootMargin: '300px 100%', scrollMargin: '0px 100%' }
    );
    this.#observeDeferredMedia();
    // A morph re-render keeps this element but strips the loaded `src`/`poster`, so re-observe.
    this.#mediaMutationObserver = new MutationObserver(this.#observeDeferredMedia);
    this.#mediaMutationObserver.observe(this, {
      subtree: true,
      childList: true,
      attributeFilter: ['src', 'poster', 'data-src', 'data-poster'],
    });

    // `autoplay` downloads the whole video at load even below the fold; play only while on screen.
    const inviewVideos = this.querySelectorAll('video[data-eq-inview-autoplay]');
    if (inviewVideos.length) {
      this.#inviewObserver = new IntersectionObserver((entries) => {
        for (const { target, isIntersecting } of entries) {
          if (isIntersecting) {
            if (target.dataset.poster && !target.poster) target.poster = target.dataset.poster;
            target.play()?.catch(() => {});
          } else {
            target.pause();
          }
        }
      }, { rootMargin: '200px 0px' });
      for (const video of inviewVideos) this.#inviewObserver.observe(video);
    }


    this.#updateProgress();
    this.#updateNavigation();
    this.#updateMuteButton();
  }

  #observeDeferredMedia = () => {
    for (const el of this.querySelectorAll('[data-src], video[data-poster]')) {
      const loaded = el.dataset.poster ? el.hasAttribute('poster') : el.hasAttribute('src');
      if (!loaded) this.#mediaObserver?.observe(el);
    }
  };

  disconnectedCallback() {
    this.#mediaMutationObserver?.disconnect();
    this.#mediaMutationObserver = null;
    this.#mediaObserver?.disconnect();
    this.#mediaObserver = null;
    this.#inviewObserver?.disconnect();
    this.#inviewObserver = null;
    this.#abortController?.abort();
    this.#abortController = null;

    if (this.#animationFrame !== null) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }

    this.#dragState = null;
    this.#suppressClickUntil = 0;

    super.disconnectedCallback();
  }

  get #cards() {
    return Array.isArray(this.refs.card)
      ? this.refs.card
      : this.refs.card
        ? [this.refs.card]
        : [];
  }

  get #slides() {
    return Array.isArray(this.refs.popupSlide)
      ? this.refs.popupSlide
      : this.refs.popupSlide
        ? [this.refs.popupSlide]
        : [];
  }

  #handleCartLinesUpdate = (event) => {
    if (event.action !== 'add') return;

    const source =
      event.target instanceof Element
        ? event.target
        : null;

    if (!source || !this.contains(source)) return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        const dialog = this.refs.dialog;

        if (
          dialog instanceof HTMLDialogElement &&
          dialog.open
        ) {
          dialog.close();
        }
      })
      .catch(() => {});
  };

  get #activeVideo() {
    const activeSlide = this.#slides[this.#activeIndex];
    const video = activeSlide?.querySelector('video');

    return video instanceof HTMLVideoElement ? video : null;
  }

  #setupDragScroll(signal) {
    const containers = this.querySelectorAll('[data-eq-drag-scroll]');

    for (const container of containers) {
      if (!(container instanceof HTMLElement)) continue;

      container.addEventListener('pointerdown', this.#handleDragStart, {
        signal,
      });

      container.addEventListener('pointermove', this.#handleDragMove, {
        signal,
      });

      container.addEventListener('pointerup', this.#handleDragEnd, {
        signal,
      });

      container.addEventListener('pointercancel', this.#handleDragCancel, {
        signal,
      });

      container.addEventListener('lostpointercapture', this.#handleLostPointerCapture, {
        signal,
      });
    }
  }

  #handleDragStart = (event) => {
    if (event.pointerType === 'touch') return;
    if (event.button !== 0) return;

    const container = event.currentTarget;

    if (!(container instanceof HTMLElement)) return;
    if (container.scrollWidth <= container.clientWidth) return;

    this.#dragState = {
      container,
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: container.scrollLeft,
      lastX: event.clientX,
      lastTime: performance.now(),
      velocity: 0,
      dragged: false,
      hasPointerCapture: false,
    };
  };

  #handleDragMove = (event) => {
    const state = this.#dragState;

    if (!state) return;
    if (event.pointerId !== state.pointerId) return;

    const delta = event.clientX - state.startX;

    if (!state.dragged && Math.abs(delta) < 6) {
      return;
    }

    if (!state.dragged) {
      state.dragged = true;

      state.container.classList.add('is-dragging');

      try {
        state.container.setPointerCapture(event.pointerId);
        state.hasPointerCapture = true;
      } catch {
        state.hasPointerCapture = false;
      }
    }

    event.preventDefault();

    const now = performance.now();
    const elapsed = Math.max(now - state.lastTime, 1);
    const movement = event.clientX - state.lastX;

    state.velocity = movement / elapsed;
    state.lastX = event.clientX;
    state.lastTime = now;

    state.container.scrollLeft =
      state.startScrollLeft - delta;

    if (state.container === this.refs.rail) {
      this.#requestProgressUpdate();
    }
  };

  #handleDragEnd = (event) => {
    const state = this.#dragState;

    if (!state) return;
    if (event.pointerId !== state.pointerId) return;

    const {
      container,
      pointerId,
      dragged,
      velocity,
      hasPointerCapture,
    } = state;

    this.#dragState = null;

    container.classList.remove('is-dragging');

    if (
      hasPointerCapture &&
      container.hasPointerCapture(pointerId)
    ) {
      container.releasePointerCapture(pointerId);
    }

    if (!dragged) {
      return;
    }

    /*
     * A browser can generate a click immediately after pointerup.
     * Suppress only that drag-generated click for a short period.
     *
     * A genuine click shortly afterwards will work normally.
     */
    this.#suppressClickUntil = performance.now() + 250;

    this.#finishDrag(container, velocity);

    if (container === this.refs.rail) {
      this.#requestProgressUpdate();
    }
  };

  #handleDragCancel = (event) => {
    const state = this.#dragState;

    if (!state) return;
    if (event.pointerId !== state.pointerId) return;

    const {
      container,
      pointerId,
      hasPointerCapture,
    } = state;

    this.#dragState = null;

    container.classList.remove('is-dragging');

    if (
      hasPointerCapture &&
      container.hasPointerCapture(pointerId)
    ) {
      container.releasePointerCapture(pointerId);
    }
  };

  #handleLostPointerCapture = (event) => {
    const state = this.#dragState;

    if (!state) return;
    if (event.pointerId !== state.pointerId) return;

    state.hasPointerCapture = false;
  };

  #finishDrag(container, velocity) {
    const children = Array.from(container.children).filter(
      (child) => child instanceof HTMLElement
    );

    if (children.length === 0) return;

    const first = children[0];
    const second = children[1];

    const step = second
      ? Math.abs(second.offsetLeft - first.offsetLeft)
      : first.offsetWidth;

    if (step <= 0) return;

    const momentum = velocity * -140;

    const projectedScroll =
      container.scrollLeft + momentum;

    const targetIndex = Math.round(
      projectedScroll / step
    );

    const maxScroll = Math.max(
      0,
      container.scrollWidth - container.clientWidth
    );

    const targetScroll = Math.min(
      maxScroll,
      Math.max(0, targetIndex * step)
    );

    container.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  }

  #requestProgressUpdate = () => {
    if (this.#animationFrame !== null) return;

    this.#animationFrame = requestAnimationFrame(() => {
      this.#animationFrame = null;
      this.#updateProgress();
    });
  };

  #updateProgress() {
    const rail = this.refs.rail;
    const progressBar = this.refs.progressBar;
    const progressTrack = this.refs.progressTrack;

    if (!(rail instanceof HTMLElement)) return;
    if (!(progressBar instanceof HTMLElement)) return;

    const maxScroll = Math.max(
      0,
      rail.scrollWidth - rail.clientWidth
    );

    const cards = this.#cards;

    if (maxScroll === 0 || cards.length <= 1) {
      progressBar.style.width = '100%';

      if (progressTrack instanceof HTMLButtonElement) {
        progressTrack.disabled = true;
      }

      return;
    }

    if (progressTrack instanceof HTMLButtonElement) {
      progressTrack.disabled = false;
    }

    const firstCard = cards[0];
    const secondCard = cards[1];

    const step = secondCard
      ? Math.abs(secondCard.offsetLeft - firstCard.offsetLeft)
      : firstCard.offsetWidth;

    const endIndex = Math.max(
      1,
      Math.round(maxScroll / Math.max(step, 1))
    );

    const currentIndex = Math.min(
      endIndex,
      Math.max(
        0,
        Math.round(rail.scrollLeft / Math.max(step, 1))
      )
    );

    const rate = Math.min(
      (currentIndex + 1) / (endIndex + 1),
      1
    );

    progressBar.style.width = `${rate * 100}%`;
  }

  handleProgressClick(event) {
    const rail = this.refs.rail;
    const progressTrack = this.refs.progressTrack;

    if (!(rail instanceof HTMLElement)) return;
    if (!(progressTrack instanceof HTMLElement)) return;

    const rect = progressTrack.getBoundingClientRect();

    const percent = Math.min(
      1,
      Math.max(
        0,
        (event.clientX - rect.left) / rect.width
      )
    );

    const maxScroll = Math.max(
      0,
      rail.scrollWidth - rail.clientWidth
    );

    rail.scrollTo({
      left: maxScroll * percent,
      behavior: 'smooth',
    });
  }

  openPopup(event) {
    /*
     * Ignore only the synthetic click immediately following
     * an actual mouse drag.
     */
    if (performance.now() < this.#suppressClickUntil) {
      return;
    }

    const clickedElement = event.target;

    if (!(clickedElement instanceof Element)) return;

    const trigger = clickedElement.closest(
      '.eq-shoppable-video__card'
    );

    const dialog = this.refs.dialog;

    if (!(trigger instanceof HTMLButtonElement)) return;
    if (!(dialog instanceof HTMLDialogElement)) return;

    const index = Number(trigger.dataset.index);

    if (
      !Number.isInteger(index) ||
      index < 0 ||
      index >= this.#slides.length
    ) {
      return;
    }

    this.#lastTrigger = trigger;
    this.#activeIndex = index;

    this.#showActiveSlide();

    if (!dialog.open) {
      dialog.showModal();
    }

    document.body.classList.add(
      'eq-shoppable-video-popup-open'
    );

    /*
     * Wait until the dialog is in the top layer before
     * starting the video.
     */
    requestAnimationFrame(() => {
      this.#playActiveVideo();
    });
  }

  closePopup() {
    const dialog = this.refs.dialog;

    if (
      dialog instanceof HTMLDialogElement &&
      dialog.open
    ) {
      dialog.close();
    }
  }

  showPrevious() {
    if (this.#activeIndex <= 0) return;

    this.#activeIndex -= 1;

    this.#showActiveSlide();
    this.#playActiveVideo();
  }

  showNext() {
    if (
      this.#activeIndex >=
      this.#slides.length - 1
    ) {
      return;
    }

    this.#activeIndex += 1;

    this.#showActiveSlide();
    this.#playActiveVideo();
  }

  toggleMute() {
    this.#isMuted = !this.#isMuted;

    const activeVideo = this.#activeVideo;

    if (activeVideo) {
      activeVideo.muted = this.#isMuted;
    }

    this.#updateMuteButton();
  }

  #showActiveSlide() {
    this.#stopAllVideos();
    this.#resetAllVideoProgress();

    this.#slides.forEach((slide, index) => {
      slide.toggleAttribute(
        'hidden',
        index !== this.#activeIndex
      );
    });

    // Posters are `data-poster` so the closed dialog's videos don't each fetch a 1080px image at load.
    const activeVideo = this.#activeVideo;
    if (activeVideo?.dataset.poster && !activeVideo.poster) {
      activeVideo.poster = activeVideo.dataset.poster;
    }

    this.#updateNavigation();
    this.#updateMuteButton();
  }

  #playActiveVideo() {
    const dialog = this.refs.dialog;
    const video = this.#activeVideo;

    if (
      !(dialog instanceof HTMLDialogElement) ||
      !dialog.open ||
      !video
    ) {
      return;
    }

    video.muted = this.#isMuted;

    const playPromise = video.play();

    if (playPromise) {
      playPromise.catch(() => {});
    }

    this.#updateMuteButton();
  }

  #stopAllVideos() {
    for (const slide of this.#slides) {
      const video = slide.querySelector('video');

      if (!(video instanceof HTMLVideoElement)) {
        continue;
      }

      video.pause();

      try {
        video.currentTime = 0;
      } catch {}
    }
  }

  #resetAllVideoProgress() {
    for (const slide of this.#slides) {
      const bar = slide.querySelector(
        '.eq-shoppable-video__video-progress-bar'
      );

      if (bar instanceof HTMLElement) {
        bar.style.width = '0%';
      }
    }
  }

  #handleVideoProgress = (event) => {
    const video = event.currentTarget;

    if (
      !(video instanceof HTMLVideoElement) ||
      !Number.isFinite(video.duration) ||
      video.duration <= 0
    ) {
      return;
    }

    const slide = video.closest(
      '.eq-shoppable-video__popup-slide'
    );

    const bar = slide?.querySelector(
      '.eq-shoppable-video__video-progress-bar'
    );

    if (!(bar instanceof HTMLElement)) {
      return;
    }

    bar.style.width = `${Math.min(
      (video.currentTime / video.duration) * 100,
      100
    )}%`;
  };

  #updateNavigation() {
    const prevButton = this.refs.prevButton;
    const nextButton = this.refs.nextButton;

    if (prevButton instanceof HTMLButtonElement) {
      prevButton.disabled =
        this.#activeIndex <= 0;
    }

    if (nextButton instanceof HTMLButtonElement) {
      nextButton.disabled =
        this.#activeIndex >=
        this.#slides.length - 1;
    }
  }

  #updateMuteButton() {
    const muteButton = this.refs.muteButton;

    if (!(muteButton instanceof HTMLButtonElement)) {
      return;
    }

    const hasVideo = Boolean(this.#activeVideo);

    muteButton.hidden = !hasVideo;
    muteButton.disabled = !hasVideo;
    muteButton.dataset.muted = String(this.#isMuted);

    muteButton.setAttribute(
      'aria-label',
      this.#isMuted
        ? this.dataset.unmuteLabel ?? ''
        : this.dataset.muteLabel ?? ''
    );
  }

  #handleDialogClose = () => {
    this.#stopAllVideos();
    this.#resetAllVideoProgress();

    document.body.classList.remove(
      'eq-shoppable-video-popup-open'
    );

    if (this.#lastTrigger instanceof HTMLElement) {
      this.#lastTrigger.focus({
        preventScroll: true,
      });
    }
  };

  #handleDialogBackdropClick = (event) => {
    if (event.target === this.refs.dialog) {
      this.closePopup();
    }
  };
}

if (
  !customElements.get(
    'eq-shoppable-video-component'
  )
) {
  customElements.define(
    'eq-shoppable-video-component',
    EqShoppableVideoComponent
  );
}